import { Field, Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import { FaExclamationCircle } from "react-icons/fa";
import AlertBox from "../../shared/Ui/AlertBox/AlertBox";
import ModalButton from "../../shared/Ui/Buttons/ModalButtons";
import style from "./index.module.css";
import { getTradePointById, inviteEmployeeToTradePoint } from "../../common/api/tradePointApi";
import SelectCustomFormik from "../../shared/Ui/SelectCustom/SelectCustomFormik";

interface InviteUserRequest {
    email: string;
    employeeName: string;
    role: string;
}

function InviteUser() {
    const navigate = useNavigate();
    const { tradePointId } = useParams();
    const [alertOpen, setAlertOpen] = useState<any>(null);
    const [tradePoint, setTradePoint] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchTradePoint = async () => {
            try {
                setIsLoading(true);
                const data = await getTradePointById(Number(tradePointId));
                setTradePoint(data);
            } catch (error) {
                console.error("Ошибка при получении данных торговой точки:", error);
                setAlertOpen({
                    status: "error",
                    message: "Ошибка при получении данных торговой точки",
                });
            } finally {
                setIsLoading(false);
            }
        };

        if (tradePointId) {
            fetchTradePoint();
        }
    }, [tradePointId]);

    const formValidationSchema = Yup.object().shape({
        employeeName: Yup.string()
            .required("Поле обязательно для заполнения")
            .min(2, "Минимум 2 символа")
            .max(50, "Максимум 50 символов"),
        email: Yup.string()
            .email("Некорректный адрес электронной почты")
            .required("Поле обязательно для заполнения"),
        role: Yup.string()
            .required("Поле обязательно для заполнения"),
    });

    async function submitInvite(formikValues: InviteUserRequest) {
        try {
            if (!tradePointId) {
                throw new Error("ID торговой точки не найден");
            }
            await inviteEmployeeToTradePoint(tradePointId, formikValues);
            setAlertOpen({
                status: "SUCCESS",
                message: "Приглашение успешно отправлено",
            });
        } catch (error: any) {
            setAlertOpen({
                status: "error",
                message: error.response?.data?.message || "Ошибка при отправке приглашения",
            });
        }
    }

    return (
        <div className="flex flex-col w-full">
            <div className="flex items-center gap-2 mb-4">

                <span className="text-xl font-semibold">Приглашение сотрудника</span>
            </div>

            {isLoading ? (
                <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
            ) : tradePoint ? (
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-lg font-semibold mb-4">Информация о торговой точке</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-sm text-gray-600">Название</p>
                            <p className="font-medium">{tradePoint.name}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">БИН</p>
                            <p className="font-medium">{tradePoint.bin}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Адрес</p>
                            <p className="font-medium">{tradePoint.address}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Телефон</p>
                            <p className="font-medium">{tradePoint.phone}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Email</p>
                            <p className="font-medium">{tradePoint.email}</p>
                        </div>
                    </div>
                </div>
            ) : null}

            <Formik
                initialValues={{
                    email: "",
                    employeeName: "",
                    role: "",
                }}
                onSubmit={submitInvite}
                validationSchema={formValidationSchema}
            >
                {(formik) => (
                    <Form className="w-full mt-[20px]">
                        <div className="flex flex-col bg-white  p-5 pt-8 relative rounded-xl shadow-md gap-[20px]">
                            <div className="gap-[20px]">
                                <div className={style.inputField1}>
                                    <div className={style.labelAndButtonWrapper}>
                                        <label className={style.inputLabel}>ФИО сотрудника</label>
                                        {formik.touched.employeeName && formik.errors.employeeName && (
                                            <div className={style.tooltipWrapper}>
                                                <FaExclamationCircle className={style.errorIcon} />
                                                <div className={style.tooltipText}>
                                                    {typeof formik.errors.employeeName === "string" ? formik.errors.employeeName : "Ошибка"}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <Field
                                        type="text"
                                        name="employeeName"
                                        placeholder="Введите имя сотрудника"
                                        className={style.placeholder}
                                    />
                                </div>


                            </div>
                            <div className="flex">
                                <div className={style.inputField1}>
                                    <div className={style.labelAndButtonWrapper}>
                                        <label className={style.inputLabel}>Email</label>
                                        {formik.touched.email && formik.errors.email && (
                                            <div className={style.tooltipWrapper}>
                                                <FaExclamationCircle className={style.errorIcon} />
                                                <div className={style.tooltipText}>
                                                    {typeof formik.errors.email === "string" ? formik.errors.email : "Ошибка"}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <Field
                                        type="email"
                                        name="email"
                                        placeholder="example@mail.com"
                                        className={style.placeholder}
                                    />
                                </div>
                            </div>
                            <div className="flex">
                                <div className={style.inputField1}>
                                    <div className={style.labelAndButtonWrapper}>
                                        <label className={style.inputLabel}>
                                        Роль в системе</label>
                                        {formik.touched.role && formik.errors.role && (
                                            <div className={style.tooltipWrapper}>
                                                <FaExclamationCircle className={style.errorIcon} />
                                                <div className={style.tooltipText}>
                                                    {typeof formik.errors.role === "string" ? formik.errors.role : "Ошибка"}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <Field
                                        component={SelectCustomFormik}
                                        name="role"
                                        options={[
                                            { value: "USER", label: "Пользователь" },
                                            { value: "ADMIN", label: "Администратор" }
                                        ]}
                                        placeholder="Выберите роль"
                                        className={style.placeholder}
                                    />
                                </div>
                            </div>


                            <div className="flex justify-end gap-[10px]">
                                <button
                                    type="button"
                                    className="bg-gray-100 text-gray-500 font-semibold py-2 px-4 rounded hover:bg-gray-200"
                                    onClick={() => navigate('/tradePoints')}
                                >
                                    Назад
                                </button>
                                <button
                                    type="submit"
                                    className="bg-mainColor text-gray-700 font-semibold py-2 px-4 rounded"
                                >
                                    Отправить приглашение
                                </button>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>

            {alertOpen && (
                <AlertBox
                    isOpen={alertOpen}
                    setIsOpen={setAlertOpen}
                />
            )}
        </div>
    );
}

export default InviteUser;
